<template>
    <UContainer>
        <div v-if="status === 'pending'">
            <PagesProductLoading />
        </div>
        <div v-else="data">

            <Head>
                <Title>{{ data?.data.seo?.title ?? data?.data.name }}</Title>
            </Head>
            <div class="flex flex-col">
                <div class="py-6">
                    <UBreadcrumb :links="breadcrumb" :ui="{ li: 'max-md:text-xs' }">
                        <template #icon="{ link }">
                            <UtilsDynamicIcon v-if="link?.icon" :icon="link?.icon"
                                class-name="w-5 h-5 fill-gray-400 text-gray-400" />
                        </template>
                    </UBreadcrumb>
                </div>
                <div class="h-full flex max-md:flex-col-reverse justify-center grow gap-8">
                    <div class="md:w-1/2 flex flex-col justify-center">
                        <div class="bg-white/75 p-4 rounded-lg shadow-79 shadow-amazon-500/15">
                            <div class="flex justify-between items-center border-b pb-2">
                                <UiComponentTypography tag="h1">
                                    {{ data?.data.name }}
                                </UiComponentTypography>
                                <div class="hidden">
                                    <div class="flex items-center relative">
                                        <div class="absolute h-full top-0 left-0 bg-yellow-500 z-10" :style="starFill">
                                        </div>
                                        <UiIconStar v-for="i in 5" class="relative z-20" fill="fill-white"
                                            stroke="stroke-2 stroke-amazon-500" />
                                    </div>
                                </div>
                            </div>
                            <p class="text-justify mt-4">
                                {{ data?.data.about }}
                            </p>
                        </div>
                        <ClientOnly v-if="data?.data.is_in_stock">
                            <WidgetsProductSelectVariantBox :max_cart="data?.data?.max_cart"
                                :min_cart="data?.data?.min_cart" :variants="data?.data?.prices"
                                v-model="selectedVariantIndex" @add-to-cart="showCartHelper = true" />
                        </ClientOnly>
                        <div v-else>
                            <div class="bg-white/75 p-4 rounded-lg mt-4 shadow-79 shadow-amazon-500/15">
                                <p class="text-2xl font-bold opacity-50 text-center">در انبار موجود نیست</p>
                            </div>
                        </div>
                        <div class="max-lg:hidden bg-amazon-50 p-4 rounded-lg shadow-79 shadow-amazon-500/15 mt-4"
                            v-if="showCartHelper">
                            <div class="flex gap-2 items-center justify-between">
                                <UiComponentTypography class-name="text-gray-600">
                                    محصول به سبد خرید شما اضافه شد.
                                </UiComponentTypography>
                                <UButton to="/cart" variant="outline"> تایید نهایی و پرداخت </UButton>
                            </div>
                        </div>
                    </div>
                    <div class="md:w-1/2 flex items-center">
                        <div class="w-full">
                            <div class="w-full aspect-square">
                                <NuxtImg
                                    v-if="primaryImage"
                                    class="w-full h-full object-contain"
                                    :alt="data?.data.name || 'محصول نرس‌لند'"
                                    :src="primaryImage"
                                    sizes="300px sm:450px lg:600px"
                                    format="webp"
                                    loading="eager"
                                    fetchpriority="high"
                                />
                            </div>
                            <div class="md:mt-6 md:px-12 w-full" v-if="gallerySlides.length > 1">
                                <ClientOnly>
                                    <LazyPagesProductSlider class="hidden lg:block" :alt-name="data?.data.name"
                                        :slides="gallerySlides" />
                                    <LazyPagesProductMobileSlider class="lg:hidden" :alt-name="data?.data.name"
                                        :slides="gallerySlides" />
                                </ClientOnly>
                            </div>
                        </div>
                    </div>
                </div>
                <div class="mt-16">
                    <PagesProductDetailSection v-if="data?.data" :product="data.data" />
                </div>
            </div>
            <PagesProductMeta v-if="data" :product="data.data" />
        </div>
    </UContainer>
</template>

<script lang="ts" setup>
import type { ISingleItem } from "~/libs/types/api/pagination";
import type { IProduct } from "~/libs/types/api/product";
import type { BreadcrumbLink } from "#ui/types";

interface IBreadcrumbSchema {
    "@type": "ListItem";
    position: number;
    name: string;
    item?: string;
}

const route = useRoute();
const { t } = useI18n();

const slug = route.params.slug;
const showCartHelper = ref(false);

const selectedVariantIndex: Ref<string | null> = ref(null);

const { data, status, error, refresh } = useAsyncData<ISingleItem<IProduct>>(
    `product-${slug}`,
    () => fetchLocal(`/products/${slug}`, {
        params: {
            include: "category,prices,gallery,attributes,variants,comments,user_price,faq",
        },
        onResponse({ response }) {
            if (response.status === 404) {
                throw showError({
                    statusCode: 404,
                    statusMessage: "Page Not Found",
                });
            }
        },
    })
);

const breadcrumbTopLvl = [
    {
        icon: "i-solar-home-angle-line-duotone",
        label: "‌",
        to: "/",
    },
    {
        label: t("categories"),
        to: "/category",
        icon: "i-solar-layers-minimalistic-line-duotone",
    },
];

const breadcrumb: ComputedRef<BreadcrumbLink[]> = computed(() => {
    const baseBreadcrumb = breadcrumbTopLvl;

    return [
        ...baseBreadcrumb,
        ...getCategoryArray(data.value?.data.category).reverse(),
        {
            label: data.value?.data.name ?? "قهوه",
            icon: "i-ph-coffee-bean-duotone",
        },
    ];
});

const sortedPrice = computed(() => {
    return useSortBy(data.value?.data.prices, [
        function (o) {
            return o.price;
        },
    ]);
});

const primaryImage = computed(() => {
    if (!data.value?.data.feature_image) return "/Images/logo/logo.png";
    const images = Object.values(data.value.data.feature_image);
    return images[0]?.original_url ? getImageUrl(images[0].original_url) : "/Images/logo/logo.png";
});

const gallerySlides = computed(() => {
    const feat = data.value?.data.feature_image ? Object.values(data.value.data.feature_image) : [];
    const gall = data.value?.data.gallery ? Object.values(data.value.data.gallery) : [];
    return [...feat, ...gall];
});

useHead(() => {
    if (!primaryImage.value || primaryImage.value === "/Images/logo/logo.png") return {};
    return {
        link: [
            {
                rel: "preload",
                href: primaryImage.value,
                as: "image",
                type: "image/webp",
                fetchpriority: "high",
            },
        ],
    };
});

useSchemaOrg([
    defineProduct({
        name: data.value?.data.name,
        image: () => primaryImage.value,
        brand: () => "Northland Co. | نرس‌لند",
        description: () => data.value?.data.about,
        sku: () => String(data.value?.data.id || slug),
        itemCondition: "https://schema.org/NewCondition",
        aggregateRating: {
            ratingValue: 100,
            bestRating: 100,
            ratingCount: 1,
        },
        review: [],
        aggregateOffer: {
            priceCurrency: "IRR",
            lowPrice: sortedPrice.value?.[0]?.price,
            highPrice: sortedPrice.value?.[sortedPrice.value.length - 1]?.price,
            offerCount: data.value?.data.prices?.length || 1,
            availability: data.value?.data.is_in_stock
                ? "https://schema.org/InStock"
                : "https://schema.org/OutOfStock",
        },
    }),
    defineBreadcrumb({
        itemListElement: () => {
            return useMap(breadcrumb.value, (item, index) => {
                const newItem: IBreadcrumbSchema = {
                    "@type": "ListItem",
                    position: index + 1,
                    name: item.label,
                };

                if (item.to) {
                    newItem.item = item.to as string;
                }

                return newItem;
            });
        },
    }),
]);

const starFill = computed(() => {
    const scores = useMap(data.value?.data.comments, (comment) => parseFloat(comment.score ?? "5"));
    const totalScore = useSum(scores);
    const averageScore = totalScore / scores.length;
    const averagePercentage = averageScore * 20;
    return {
        width: `${averagePercentage}%`,
    };
});
</script>

<style></style>
